/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  useContext, useEffect, useMemo, useState, useRef
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  Typography,
  Alert,
  TextField, IconButton, Tooltip, Link
} from '@one-thd/sui-atomic-components';
import { Info } from '@one-thd/sui-icons';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  extend, useLazyDataModel,
} from '@thd-nucleus/data-sources';
import { QuickIntentResolve } from '@thd-olt-component-react/pro-planning-tools';
import { GroupedIntents } from './GroupedIntents';
import { AddToContext } from '../../AddToContext';
import { TRACK } from '../../AddToConstants';
import { Feedback } from './Feedback';
import {
  generateId,
  getCurrentIntents,
  getSelectedMaterials,
  triggerAnalytics
} from '../../AddToHelpers';
import {
  resolveIntentFromItem, getAddToListAddActionForProjectsAnalytics
} from '../../../utils/intents.utils';
import { useListMetrics } from '../../hooks/useListMetrics';
import { IntentsPreviewFooter } from './IntentPreviewFooter';

const updateGroupItem = (groups, groupId, id, updatedFields) => {
  return (groups || []).map((group) => {
    if (group.id !== groupId) return group;
    const itemExists = group.materials.some((item) => item.id === id);
    if (!itemExists) {
      return { ...group, materials: [...group.materials, { id, ...updatedFields }] };
    }
    return {
      ...group,
      materials: group.materials.map((item) => (item.id === id ? { ...item, ...updatedFields } : item))
    };
  });
};

const updateBulkGroupItems = (groups, groupId, updatedItems) => {
  return (groups || []).map((group) => {
    if (group.id !== groupId) return group;
    return {
      ...group,
      materials: group.materials.map((item) => (updatedItems[item.id] ? { ...item, ...updatedItems[item.id] } : item))
    };
  });
};

const updateAllSelections = (groups, isSelected) => {
  return (groups || []).map((group) => ({
    ...group,
    materials: group.materials.map((item) => ({ ...item, isSelected }))
  }));
};

export const IntentsPreview = ({
  onNewIntentsSave, refId, refType, projectId = null, listTitle = '',
}) => {
  const [showAddGroupInput, setShowAddGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const { channel } = useContext(ExperienceContext);

  const isMobile = channel === 'mobile';
  const accept = useRef(false);
  const isProjects = refType === 'PROJECT';
  const [isAutoResolving, setIsAutoResolving] = useState(false);
  const [resolvedIntentItemMap, setResolvedIntentItemMap] = useState({});
  const [selectionStrategy, setSelectionStrategy] = useState('');
  const [estimatedMaterials, setEstimatedMaterials] = useState(null);
  const [quantityEstimationFailed, setQuantityEstimationFailed] = useState(false);
  const enableQuantityEstimation = useConfigService('fs:isAiQuantityEstimationEnabled');

  const {
    groupedIntentsPreview,
    generatedIntents,
    setGroupedIntentsPreview,
    setScreen,
    handleClose,
    previousScreen,
    mcvisId,
    storeId,
    aiPrompt,
    questionnaireAnswers
  } = useContext(AddToContext);

  const {
    groupedIntents, sessionId = '', sourceType, starterListName, aiInputMethod, aiInputType
  } = groupedIntentsPreview || {};

  const [submitFeedback, { loading, data, error }] = useLazyDataModel('interactWithAgent', {
    variables: {
      interactionType: 'FEEDBACK',
      metaData: {
        sessionId,
        channel,
        refType,
        refId,
        mcvis: mcvisId || '',
      }
    },
    context: {
      withAuth: true
    }
  });

  const listDataForMetrics = refId && storeId ? { id: refId, storeId } : {};
  const { myListInformation } = useListMetrics([], listDataForMetrics);

  const intentsToResolve = useMemo(() => {
    const intents = (groupedIntents || []).flatMap((group) => group.materials)
      .filter((item) => item?.intent?.trim().length > 0 && !item?.itemId && item?.isSelected)
      .map((item) => item?.intent);
    return Array.from(new Set(intents));
  }, [groupedIntents, generatedIntents]);
  useEffect(() => {
    // 10.4 Customize Your List Overlay - Track when customer lands on the overlay
    triggerAnalytics(TRACK.LIST_BUILDER_CUSTOMIZE_LIST_OVERLAY, {
      aiSessionId: sessionId
    });

    return () => {
      if (!accept.current) {
        setGroupedIntentsPreview({ ...groupedIntentsPreview, groupedIntents: [] });
        // If the user navigates away without accepting, send discard feedback
        const selectedMaterials = getSelectedMaterials(groupedIntents);
        const currentIntents = getCurrentIntents(generatedIntents, selectedMaterials);
        accept.current = false;
        submitFeedback({
          variables: {
            feedbackData: {
              type: null,
              accept_discard: 'DISCARD',
              category: null,
              comment: null,
              currentIntents,
              selectionStrategy: null,
              listState: null
            }
          }
        }).catch(() => {});
      }
    };
  }, []);

  useEffect(() => {
    if ((data || error) && !loading && accept.current) {
      let selectedMaterials = getSelectedMaterials(estimatedMaterials || groupedIntents);
      if (onNewIntentsSave) {
        const isQuickAutoResolve = Object.keys(resolvedIntentItemMap).length > 0;
        if (isQuickAutoResolve) {
          selectedMaterials = resolveIntentFromItem(selectedMaterials, resolvedIntentItemMap, isProjects);
        }

        if (isProjects) {
          const projectsAnalyticsInfo = getAddToListAddActionForProjectsAnalytics({
            selectedMaterials,
            sourceType,
            sessionId,
            aiInputMethod,
            aiInputType,
            starterListName,
            projectId,
            isQuickAutoResolve
          });
          triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, projectsAnalyticsInfo);
        }
        const showResolvedProducts = selectionStrategy === 'AUTO_SELECTION';
        onNewIntentsSave(selectedMaterials, showResolvedProducts, quantityEstimationFailed)?.finally(() => {
          handleClose();
        });
      }
    }
  }, [data, error, loading]);

  const count = useMemo(() => {
    let selected = 0;
    let all = 0;
    (groupedIntents || []).forEach((group) => {
      (group.materials || []).forEach((item) => {
        all += 1;
        if (item.isSelected) {
          selected += 1;
        }
      });
    });
    return { selected, all };
  }, [groupedIntents]);

  const onItemUpdate = (groupId, id, updatedFields) => {
    setGroupedIntentsPreview((prevIntents) => ({
      ...prevIntents,
      groupedIntents: updateGroupItem(prevIntents?.groupedIntents, groupId, id, updatedFields)
    }));
  };

  const onBulkItemUpdate = (groupId, updatedItems) => {
    setGroupedIntentsPreview((prevIntents) => ({
      ...prevIntents,
      groupedIntents: updateBulkGroupItems(prevIntents?.groupedIntents, groupId, updatedItems)
    }));
  };

  const onAddGroup = () => {
    setShowAddGroupInput(true);
  };

  const handleSaveGroup = () => {
    const newGroupTrimmed = newGroupName.trim();
    if (newGroupTrimmed) {
      const newGroupId = generateId();
      const newGroup = {
        id: newGroupId,
        groupName: newGroupTrimmed,
        itemOrder: 1,
        materials: [],
        __typename: 'MaterialsGroup'
      };

      // Add the new group at the beginning and update itemOrder for existing groups
      setGroupedIntentsPreview((prevIntents) => {
        const updatedExistingGroups = (prevIntents?.groupedIntents || []).map((group) => ({
          ...group,
          itemOrder: group.itemOrder + 1
        }));
        return {
          ...prevIntents,
          groupedIntents: [newGroup, ...updatedExistingGroups]
        };
      });
    }

    // Reset state and hide input
    setNewGroupName('');
    setShowAddGroupInput(false);
  };

  const handleCancelAddGroup = () => {
    setNewGroupName('');
    setShowAddGroupInput(false);
  };

  const handleSelectAll = () => {
    const unselect = count.all === count.selected;
    setGroupedIntentsPreview((prevIntents) => ({
      ...prevIntents,
      groupedIntents: updateAllSelections(prevIntents?.groupedIntents, !unselect)
    }));
  };

  const handleAccept = async () => {
    try {
      const selectedMaterials = getSelectedMaterials(groupedIntents);
      const currentIntents = getCurrentIntents(generatedIntents, selectedMaterials);

      // 6.13.1 Add to List action - Use metrics from existing list items only
      if (selectedMaterials && selectedMaterials.length > 0 && !isProjects) {
        const { uniqueSkuCount, quantityTotal, listTotal } = myListInformation;

        triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
          name: 'list add',
          location: 'INTENTS_PREVIEW',
          items: selectedMaterials.map((material) => ({
            quantity: material.quantity || 1,
            price: 0,
            sku: material.id,
            intentStatus: 'yes',
            resolvedFromIntent: 'no',
            aiQuantityEstimate: material?.aiQuantityEstimate ? 'yes' : 'no'
          })),
          isBulkAction: selectedMaterials.length > 1,
          listTotal, // Only actual products
          uniqueSkuCount, // Only actual products with SKUs
          quantityTotal, // Only actual products with quantities
          // Pass raw analytics context to thd-analytics component
          sourceType,
          sessionId,
          starterListName,
          aiInputMethod,
          aiInputType,
        });
      }

      const { totalListItemCount } = myListInformation;
      const listState = totalListItemCount > 0 ? 'EXISTING' : 'NEW';

      accept.current = true;
      await submitFeedback({
        variables: {
          feedbackData: {
            type: null,
            accept_discard: 'ACCEPT',
            category: null,
            comment: null,
            currentIntents,
            selectionStrategy: selectionStrategy || 'MANUAL_SELECTION',
            listState
          }
        }
      });
    } catch (errorCatched) {
      // eslint-disable-next-line no-console
      console.error('Error accepting intents:', errorCatched);
    }
  };

  const onAllResolved = async (
    resolvedIntentItemMapReceived, estimatedMaterialsReceived, estimationFailed
  ) => {
    setResolvedIntentItemMap(resolvedIntentItemMapReceived);
    setEstimatedMaterials(estimatedMaterialsReceived);
    setQuantityEstimationFailed(!!estimationFailed);
    await handleAccept();
  };

  if (isAutoResolving) {
    return (
      <div className="sui-p-5">
        <QuickIntentResolve
          intents={intentsToResolve}
          onAllResolved={onAllResolved}
          showLoader
          showLoaderOnComplete
          enableQuantityEstimation={enableQuantityEstimation}
          intentGroups={groupedIntents}
          listTitle={listTitle}
          aiPromptMessage={aiPrompt}
          questionnaireAnswers={questionnaireAnswers}
          estimationMetaData={{
            refType,
            refId,
            channel
          }}
        />
      </div>
    );
  }

  return (
    <>
      <DrawerBody>
        {!(isMobile && !isProjects) && (
          <div className="sui-w-full">
            <Alert
              status="warning"
              size="large"
              headline="This is just a preview of your list"
            >
              Continue to the next page to see our recommended products for each item
            </Alert>
          </div>
        )}
        <div className="sui-p-2 sui-grid sui-justify-items-end">
          <div className="sui-flex sui-flex-row sui-mt-auto">
            <Tooltip
              placement="bottom-start"
              title={`Product list generated by AI; please verify against your job requirements.
               The Home Depot is not responsible for the accuracy of this product list.`}
            >
              <IconButton color="info" icon={Info} />
            </Tooltip>
            <div className="sui-pt-2">
              <Typography variant="body-xs" height="tight" paragraph>
                Product list generated by AI
              </Typography>
            </div>
          </div>
        </div>
        <div className="sui-mt-2 sui-px-2 sui-flex sui-flex-row sui-w-full">
          <div className="sui-flex sui-flex-col sui-gap-2 sui-w-[150px]">
            <Typography variant="body-base" weight="bold">{count.selected} Selected</Typography>
            <Link
              align="left"
              component="button"
              onClick={handleSelectAll}
            >
              {count.all === count.selected ? 'Unselect All' : 'Select All'}
            </Link>
          </div>
          <div
            className="sui-flex sui-justify-end sui-items-center sui-gap-2 sui-w-full"
          >
            {showAddGroupInput ? (
              <div className="sui-flex sui-w-full sui-flex-row sui-gap-2 sui-align-middle">
                <TextField
                  value={newGroupName}
                  onChange={(event) => setNewGroupName(event.target.value)}
                  placeholder="Enter group name"
                  autoFocus
                  fullWidth
                />
                <div className="sui-flex sui-justify-end sui-items-center sui-gap-2 sui-pt-2">
                  <Button
                    variant="primary"
                    size="small"
                    onClick={handleSaveGroup}
                  >
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={handleCancelAddGroup}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="sui-mt-2">
                <Button
                  variant="ghost"
                  onClick={onAddGroup}
                >
                  + Add Group
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="sui-mt-2">
          {
            (groupedIntents || []).map((group, __index) => (
              <div key={`group-${group.id}`} className="sui-mb-2">
                <GroupedIntents
                  index={__index}
                  group={group}
                  onItemUpdate={onItemUpdate}
                  onBulkItemUpdate={onBulkItemUpdate}
                />
              </div>
            ))
          }
        </div>
        <Feedback refId={refId} refType={refType} />
      </DrawerBody>
      <DrawerFooter
        position="center"
        style={{ boxShadow: '0 1px 8px 0 rgb(0 0 0 / 0.375)' }}
      >
        <IntentsPreviewFooter
          handleAccept={handleAccept}
          loading={loading}
          setIsAutoResolving={setIsAutoResolving}
          setSelectionStrategy={setSelectionStrategy}
        />
      </DrawerFooter>
    </>
  );
};

IntentsPreview.displayName = 'IntentsPreview';

IntentsPreview.propTypes = {
  onNewIntentsSave: PropTypes.func.isRequired,
  refType: PropTypes.string,
  refId: PropTypes.string,
  projectId: PropTypes.string,
  listTitle: PropTypes.string,
};

IntentsPreview.defaultProps = {
  refId: '',
  refType: '',
  projectId: null,
  listTitle: '',
};

IntentsPreview.dataModel = extend(
  Feedback,
  useListMetrics.dataModel,
  QuickIntentResolve
);
